/**
 * <s-addon-offer> — adds the paired product (beanie or tee) to the cart without leaving the page,
 * then opens the cart drawer. The saving itself comes from the store's automatic discount.
 *
 * With data-mobile-popup, phones see the card pop up as a bottom sheet shortly after the page opens
 * (once per product per browser session). Closing it returns the card to its inline spot.
 */
const MOBILE_QUERY = '(max-width: 47.999rem)';

class SAddonOffer extends HTMLElement {
  #timer;
  #dialog;
  #placeholder;
  #shown = false;

  connectedCallback() {
    this.form = this.querySelector('[data-addon-form]');
    this.submitButton = this.querySelector('[data-addon-submit]');
    this.errorEl = this.querySelector('[data-addon-error]');
    this.sizeHint = this.querySelector('[data-addon-size-hint]');
    this.form?.addEventListener('submit', this.#onSubmit);
    this.form?.addEventListener('change', this.#hideSizeHint);

    if (this.#dialog || this.#shown) return; // moved into or back from the sheet; already set up
    if (this.hasAttribute('data-mobile-popup') && this.#shouldPopUp()) {
      const delay = Number(this.dataset.popupDelay) || 1200;
      this.#timer = window.setTimeout(() => this.#openSheet(), delay);
    }
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
    this.form?.removeEventListener('change', this.#hideSizeHint);
    if (!this.#dialog) window.clearTimeout(this.#timer);
  }

  get #storageKey() {
    return `gw-addon-offer-seen-${this.dataset.productId || ''}`;
  }

  #shouldPopUp() {
    if (!window.matchMedia(MOBILE_QUERY).matches) return false;
    if (this.hasAttribute('data-design-mode')) return true;
    try {
      return !window.sessionStorage.getItem(this.#storageKey);
    } catch {
      return true;
    }
  }

  #openSheet() {
    if (document.querySelector('dialog[open]')) return;
    if (!window.matchMedia(MOBILE_QUERY).matches) return;

    this.#shown = true;
    const dialog = document.createElement('dialog');
    dialog.className = 'addon-sheet';
    dialog.setAttribute('aria-label', this.dataset.sheetLabel || '');

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'addon-sheet__close';
    closeButton.textContent = this.dataset.closeLabel || 'Close';
    closeButton.addEventListener('click', () => dialog.close());

    const bar = document.createElement('div');
    bar.className = 'addon-sheet__bar';
    bar.append(closeButton);

    this.#placeholder = document.createComment('addon-offer');
    this.#dialog = dialog;
    this.replaceWith(this.#placeholder);
    dialog.append(bar, this);
    document.body.append(dialog);

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close(); // tap on the backdrop
    });
    dialog.addEventListener('close', () => this.#returnInline(), { once: true });

    try { window.sessionStorage.setItem(this.#storageKey, '1'); } catch { /* storage unavailable */ }
    dialog.showModal();
  }

  #returnInline() {
    const dialog = this.#dialog;
    this.#dialog = null;
    if (this.#placeholder?.isConnected) this.#placeholder.replaceWith(this);
    this.#placeholder = null;
    dialog?.remove();
  }

  #hideSizeHint = () => {
    if (this.sizeHint) this.sizeHint.hidden = true;
  };

  #onSubmit = async (event) => {
    event.preventDefault();
    const variantId = String(new FormData(this.form).get('id') || '').trim();
    if (!variantId) {
      if (this.sizeHint) this.sizeHint.hidden = false;
      return;
    }

    this.#showError('');
    this.submitButton.disabled = true;
    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: variantId, quantity: 1 }] },
        { event: { context: 'product', detail: { source: 's-addon-offer' } } }
      );
      const userError = result?.userErrors?.[0]?.message;
      if (userError) {
        this.#showError(userError);
      } else {
        this.#dialog?.close();
        await window.Shopify.actions.openCart?.();
      }
    } catch (error) {
      this.#showError(error?.message || this.dataset.errorMessage || '');
    } finally {
      this.submitButton.disabled = false;
    }
  };

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }
}

if (!customElements.get('s-addon-offer')) customElements.define('s-addon-offer', SAddonOffer);
